import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  inject,
} from '@angular/core';
import * as d3 from 'd3';

export type Orientation = 'top' | 'bottom' | 'left' | 'right';
@Directive({
  selector: '[bshAxis]',
})
export class AxisDirective implements OnChanges, OnDestroy {
  #el = inject(ElementRef);
  private isHidden = false;
  private readonly observer: ResizeObserver;

  // eslint-disable-next-line
  @Input() scale!: d3.AxisScale<any>;
  @Input() orient: Orientation = 'bottom';
  @Input() hideTicks = false;
  @Input() hideLabels = false;
  // eslint-disable-next-line
  @Input() tickFormat?: (d: any) => string;

  constructor() {
    this.observer = new ResizeObserver(entries => {
      const contentRect = entries[0].contentRect;
      const labels = d3.select(this.#el.nativeElement).selectAll('.tick text');
      if (
        !this.hideTicks &&
        (this.orient === 'bottom' || this.orient === 'top')
      ) {
        if (contentRect.width > 0 && contentRect.width < labels.size() * 50) {
          // rotate labels if the width is too small
          labels
            .style('text-anchor', 'end')
            .attr('dx', '-.8em')
            .attr('dy', '.15em')
            .attr('transform', 'rotate(-65)');
        } else {
          labels
            .style('display', 'block')
            .style('text-anchor', 'middle')
            .attr('dx', '0')
            .attr('dy', '0')
            .attr('transform', 'translate(0, 5)');
        }
      }
    });
    this.observer.observe(this.#el.nativeElement);
  }

  ngOnChanges(): void {
    this.renderAxis();
  }

  ngOnDestroy() {
    this.observer.unobserve(this.#el.nativeElement);
    this.observer.disconnect();
  }

  private renderAxis(): void {
    // eslint-disable-next-line
    let axisFn: d3.Axis<any>;
    switch (this.orient) {
      case 'top':
        axisFn = d3.axisTop(this.scale);
        break;
      case 'right':
        axisFn = d3.axisRight(this.scale);
        break;
      case 'left':
        axisFn = d3.axisLeft(this.scale);
        break;
      default:
        axisFn = d3.axisBottom(this.scale);
    }

    if (this.tickFormat) {
      axisFn.tickFormat(this.tickFormat);
    }

    d3.select(this.#el.nativeElement).call(axisFn);

    // Hide ticks if requested
    if (this.hideTicks) {
      d3.select(this.#el.nativeElement)
        .selectAll('.tick line')
        .style('display', 'none');
      this.isHidden = true;
    } else if (this.isHidden) {
      d3.select(this.#el.nativeElement)
        .selectAll('.tick line')
        .style('display', 'block');
    }
    // Hide labels if requested
    if (this.hideLabels) {
      d3.select(this.#el.nativeElement)
        .selectAll('.tick text')
        .style('display', 'none');
      this.isHidden = true;
    } else if (this.isHidden) {
      d3.select(this.#el.nativeElement)
        .selectAll('.tick text')
        .style('display', 'block');
    }
  }
}
